/**
 * Type guard utilities for validating status and role values at runtime
 */

import {
  StationStatus,
  SessionStatus,
  TransactionStatus,
  UserStatus,
  UserRole,
  ConnectorStatus,
} from '../enums/status';

// ============================================
// Station Status Type Guards
// ============================================

/**
 * Type guard for StationStatus
 */
export const isStationStatus = (value: string | undefined): value is StationStatus => {
  if (!value) return false;
  return Object.values(StationStatus).includes(value.toUpperCase() as StationStatus);
};

/**
 * Safely convert a string to StationStatus with fallback
 */
export const toStationStatus = (
  value: string | undefined,
  fallback: StationStatus = StationStatus.INACTIVE,
): StationStatus => {
  if (!value) return fallback;
  const upper = value.toUpperCase();
  if (Object.values(StationStatus).includes(upper as StationStatus)) {
    return upper as StationStatus;
  }
  return fallback;
};

// ============================================
// Connector Status Type Guards
// ============================================

/**
 * Type guard for ConnectorStatus
 */
export const isConnectorStatus = (value: string | undefined): value is ConnectorStatus => {
  if (!value) return false;
  return Object.values(ConnectorStatus).includes(value as ConnectorStatus);
};

/**
 * Safely convert a string to ConnectorStatus with fallback
 */
export const toConnectorStatus = (
  value: string | undefined,
  fallback: ConnectorStatus = ConnectorStatus.UNAVAILABLE,
): ConnectorStatus => {
  if (!value) return fallback;
  if (Object.values(ConnectorStatus).includes(value as ConnectorStatus)) {
    return value as ConnectorStatus;
  }
  // Handle case variations
  const normalized = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  if (Object.values(ConnectorStatus).includes(normalized as ConnectorStatus)) {
    return normalized as ConnectorStatus;
  }
  return fallback;
};

// ============================================
// Session Status Type Guards
// ============================================

/**
 * Type guard for SessionStatus
 */
export const isSessionStatus = (value: string | undefined): value is SessionStatus => {
  if (!value) return false;
  return Object.values(SessionStatus).includes(value.toLowerCase() as SessionStatus);
};

/**
 * Safely convert a string to SessionStatus with fallback
 */
export const toSessionStatus = (
  value: string | undefined,
  fallback: SessionStatus = SessionStatus.PREPARE,
): SessionStatus => {
  if (!value) return fallback;
  const lower = value.toLowerCase();
  if (Object.values(SessionStatus).includes(lower as SessionStatus)) {
    return lower as SessionStatus;
  }
  return fallback;
};

// ============================================
// Transaction Status Type Guards
// ============================================

/**
 * Type guard for TransactionStatus
 */
export const isTransactionStatus = (value: string | undefined): value is TransactionStatus => {
  if (!value) return false;
  return Object.values(TransactionStatus).includes(value.toLowerCase() as TransactionStatus);
};

/**
 * Safely convert a string to TransactionStatus with fallback
 */
export const toTransactionStatus = (
  value: string | undefined,
  fallback: TransactionStatus = TransactionStatus.CREATED,
): TransactionStatus => {
  if (!value) return fallback;
  const lower = value.toLowerCase();
  if (Object.values(TransactionStatus).includes(lower as TransactionStatus)) {
    return lower as TransactionStatus;
  }
  return fallback;
};

// ============================================
// User Status Type Guards
// ============================================

/**
 * Type guard for UserStatus
 */
export const isUserStatus = (value: string | undefined): value is UserStatus => {
  if (!value) return false;
  return Object.values(UserStatus).includes(value.toLowerCase() as UserStatus);
};

/**
 * Safely convert a string to UserStatus with fallback
 */
export const toUserStatus = (
  value: string | undefined,
  fallback: UserStatus = UserStatus.INACTIVE,
): UserStatus => {
  if (!value) return fallback;
  const lower = value.toLowerCase();
  if (Object.values(UserStatus).includes(lower as UserStatus)) {
    return lower as UserStatus;
  }
  return fallback;
};

// ============================================
// User Role Type Guards
// ============================================

/**
 * Type guard for UserRole
 */
export const isUserRole = (value: string | undefined): value is UserRole => {
  if (!value) return false;
  return Object.values(UserRole).includes(value.toLowerCase() as UserRole);
};

/**
 * Safely convert a string to UserRole with fallback
 */
export const toUserRole = (
  value: string | undefined,
  fallback: UserRole = UserRole.USER,
): UserRole => {
  if (!value) return fallback;
  const lower = value.toLowerCase();
  if (Object.values(UserRole).includes(lower as UserRole)) {
    return lower as UserRole;
  }
  return fallback;
};
